import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { Form } from 'factorial-form'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

// Hook
import createMemo from 'modules/core/hooks/createMemo'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { normalizeText } from 'modules/core/lib/stringUtils'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'

import employees from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { getDashboardAuthor } from 'modules/reports/lib/getDashboardAuthor'
import dashboards from 'modules/reports/store/collections/dashboards'
import type DashboardModel from 'modules/reports/store/models/Dashboard'

import Content from './components/_Content'
import ListDashboards from './components/_ListDashboards'

export interface RenderProps {
  dashboard: DashboardModel
}
interface Props {
  loading: boolean
}

const useOrderDashboards = createMemo((form, dashboards) => {
  const sortAttribute: string = form.get('sortAttribute').value
  const sortDirection: string = form.get('sortDirection').value

  return orderBy(
    dashboards,
    [
      (dashboard: DashboardModel): string => {
        if (sortAttribute === 'title') {
          return normalizeText(dashboard.attributes.title)
        }

        if (sortAttribute === 'author') {
          const author = getDashboardAuthor(dashboard)
          return normalizeText(
            author
              ? getFullDisplayName({
                  firstName: author.attributes.first_name,
                  lastName: author.attributes.last_name,
                })
              : ''
          )
        }

        if (sortAttribute === 'created_at') {
          return dashboard.attributes.created_at
        }

        return dashboard.attributes.updated_at
      },
    ],
    [sortDirection as 'asc' | 'desc']
  )
})

const DashboardList = ({ loading }: Props) => {
  const filtersForm = React.useMemo(
    () =>
      new Form(
        {
          sortAttribute: 'updated_at',
          sortDirection: 'desc',
        },
        {
          sortAttribute: 'string',
          sortDirection: 'string',
        }
      ),
    []
  )

  const dashboardList = useOrderDashboards(filtersForm, dashboards.toArray())
  const { employee: currentEmployeeGraphQL } = useCurrentSession()
  const currentEmployee = currentEmployeeGraphQL
    ? employees.find({ id: currentEmployeeGraphQL.id })
    : undefined
  const { value: canManageReports } = useAsyncCan('reports.manage', currentEmployee || undefined)

  const showAddDashboardButton = !loading && canManageReports

  return (
    <ResponsivePage.Body error={undefined} loading={loading} showHeader={false}>
      <Content>
        <ListDashboards
          showAddDashboardButton={showAddDashboardButton}
          filtersForm={filtersForm}
          dashboards={dashboardList}
        />
      </Content>
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default observer(DashboardList)
